import type { Workflow } from '@n8n-builder/n8n-types';

export interface ValidationResult {
  isValid: boolean;
  workflow?: Workflow;
  errors: string[];
  nodeCount?: number;
}

/**
 * Validate a JSON string as an n8n workflow.
 * Used for client-side validation of uploaded workflow files.
 */
export function validateWorkflowJson(content: string): ValidationResult {
  const errors: string[] = [];

  // Parse JSON
  let parsed: unknown;
  try {
    parsed = JSON.parse(content);
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Unknown error';
    return {
      isValid: false,
      errors: [`Invalid JSON syntax: ${message}`],
    };
  }

  // Check it's an object
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return {
      isValid: false,
      errors: ['Not a valid n8n workflow. Expected an object.'],
    };
  }

  const obj = parsed as Record<string, unknown>;

  // Check required fields
  if (typeof obj.name !== 'string') {
    errors.push('Missing or invalid "name" field (expected string)');
  }

  if (!Array.isArray(obj.nodes)) {
    errors.push('Missing or invalid "nodes" field (expected array)');
  }

  if (typeof obj.connections !== 'object' || obj.connections === null || Array.isArray(obj.connections)) {
    errors.push('Missing or invalid "connections" field (expected object)');
  }

  // If required fields are missing, return early
  if (errors.length > 0) {
    return {
      isValid: false,
      errors: ['Not a valid n8n workflow. ' + errors.join('. ')],
    };
  }

  // Validate nodes have required fields
  const nodes = obj.nodes as unknown[];
  const nodeErrors: string[] = [];

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (typeof node !== 'object' || node === null || Array.isArray(node)) {
      nodeErrors.push(`Node at index ${i} is not a valid object`);
      continue;
    }

    const nodeObj = node as Record<string, unknown>;
    if (typeof nodeObj.type !== 'string') {
      nodeErrors.push(`Node at index ${i} is missing "type" field`);
    }
    if (typeof nodeObj.name !== 'string') {
      nodeErrors.push(`Node at index ${i} is missing "name" field`);
    }
  }

  if (nodeErrors.length > 0) {
    return {
      isValid: false,
      errors: nodeErrors,
    };
  }

  // Construct the workflow object
  const workflow: Workflow = {
    id: typeof obj.id === 'string' ? obj.id : crypto.randomUUID(),
    name: obj.name as string,
    active: typeof obj.active === 'boolean' ? obj.active : false,
    nodes: obj.nodes as Workflow['nodes'],
    connections: obj.connections as Workflow['connections'],
    settings: typeof obj.settings === 'object' ? obj.settings as Workflow['settings'] : undefined,
  };

  return {
    isValid: true,
    workflow,
    errors: [],
    nodeCount: workflow.nodes.length,
  };
}
